import { useQuery } from "@tanstack/vue-query";
import { type Ref } from "vue";
import { api } from "@/api";
import { type BaseReportFilters } from "./reports";

// Fast Storage reports (PLAN 10). Every count is of OPENED crossings: the
// customer confirmed it and the compound gate opened.

export const REPORT_STORAGE_QUERY_KEY = "storage-reports";

type Filters = BaseReportFilters | Ref<BaseReportFilters>;

const useStorageReport = <T>(name: string, path: string, filters: Filters) =>
  useQuery({
    queryKey: [REPORT_STORAGE_QUERY_KEY, name, filters] as const,
    queryFn: async ({ queryKey: [, , query] }) => {
      const { body } = await api.get<T>(path, { query: unref(query) });
      return body;
    },
    staleTime: 60 * 60 * 1000,
  });

const exportStorageReport = async (path: string, query: BaseReportFilters) => {
  const data = await api.get<Blob>(path, { query: query });
  return exportFileFromRespone(data);
};

// Movements per outfit

export type StorageMovementRow = {
  bookingId: string;
  siteName: string;
  bookingRef: string;
  customerName: string;
  membershipNumber: string | null;
  pitchName: string;
  kind: string;
  startDate: string;
  endDate: string;
  totalVehicleIn: number;
  totalVehicleOut: number;
  totalPedestrianIn: number;
  totalPedestrianOut: number;
  totalIn: number;
  totalOut: number;
  totalMovements: number;
};

export type StorageMovementTotals = Omit<
  StorageMovementRow,
  | "bookingId"
  | "siteName"
  | "bookingRef"
  | "customerName"
  | "membershipNumber"
  | "pitchName"
  | "kind"
  | "startDate"
  | "endDate"
> & {
  totalOutfits: number;
  totalOutfitsMoved: number;
};

export type StorageMovementsReport = {
  totals: StorageMovementTotals;
  rows: StorageMovementRow[];
};

export const useStorageMovementsReport = (filters: Filters) =>
  useStorageReport<StorageMovementsReport>(
    "movements",
    "/reports/storage/movements/",
    filters,
  );

export const exportStorageMovementsReport = (query: BaseReportFilters) =>
  exportStorageReport("/reports/storage/movements/export/", query);

// Busy times

export type StorageAccessTime = {
  dimension: "hour" | "weekday" | "month";
  bucket: number;
  label: string;
  totalIn: number;
  totalOut: number;
  total: number;
};

export type StorageAccessTimesReport = {
  hours: StorageAccessTime[];
  weekdays: StorageAccessTime[];
  months: StorageAccessTime[];
};

export const useStorageAccessTimesReport = (filters: Filters) =>
  useStorageReport<StorageAccessTimesReport>(
    "access-times",
    "/reports/storage/access-times/",
    filters,
  );

export const exportStorageAccessTimesReport = (query: BaseReportFilters) =>
  exportStorageReport("/reports/storage/access-times/export/", query);

// Time in the compound

export type StorageVisit = {
  siteName: string;
  bookingRef: string;
  customerName: string;
  pitchName: string | null;
  kind: string | null;
  enteredAt: string;
  leftAt: string;
  enteredOn: "vehicle" | "pedestrian";
  leftOn: "vehicle" | "pedestrian";
  durationMinutes: number;
  overstayAfterMinutes: number;
  isOverstay: boolean;
};

export type StorageCompoundTimeReport = {
  summary: {
    totalVisits: number;
    medianMinutes: number | null;
    maxMinutes: number | null;
    totalOverstays: number;
  };
  visits: StorageVisit[];
};

export const useStorageCompoundTimeReport = (filters: Filters) =>
  useStorageReport<StorageCompoundTimeReport>(
    "compound-time",
    "/reports/storage/compound-time/",
    filters,
  );

export const exportStorageCompoundTimeReport = (query: BaseReportFilters) =>
  exportStorageReport("/reports/storage/compound-time/export/", query);

// Storage against touring: aggregate only, customer consent is not captured yet

export type StorageVsTouringRow = {
  siteName: string;
  totalOutPeriods: number;
  totalTouringHere: number;
  percentageTouringHere: string;
  totalTouringElsewhere: number;
  percentageTouringElsewhere: string;
  totalTouringAny: number;
  percentageTouringAny: string;
};

export const useStorageVsTouringReport = (filters: Filters) =>
  useStorageReport<StorageVsTouringRow[]>(
    "vs-touring",
    "/reports/storage/vs-touring/",
    filters,
  );

export const exportStorageVsTouringReport = (query: BaseReportFilters) =>
  exportStorageReport("/reports/storage/vs-touring/export/", query);
